import { DatePickerInput, DatePickerInputProps } from '@mantine/dates'
import 'dayjs/locale/pl'
import { dayjsLocaleCodes } from 'hooks/useDayjs'
import { Ref, forwardRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { TbCalendar } from 'react-icons/tb'

export const InputDate = ({
  name,
  icon: Icon,
  required,
  ...props
}: Omit<
  DatePickerInputProps,
  'onChange' | 'value' | 'name' | 'icon' | 'ref'
> & {
  name: string
  icon?: IconType
}) => {
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { error } = fieldState
        return (
          <InputDatePure
            {...field}
            {...props}
            error={error?.message}
            leftSection={
              Icon ? <Icon size={'1.2em'} /> : <TbCalendar size={'1.2em'} />
            }
            withAsterisk={required}
          />
        )
      }}
    />
  )
}

const InputDatePure = forwardRef(
  (
    props: Omit<DatePickerInputProps, 'onChange' | 'value' | 'icon' | 'ref'> & {
      onChange: (_date: string | undefined) => void
      value?: string
    },
    ref: Ref<HTMLButtonElement>,
  ) => {
    const { i18n } = useTranslation()

    const code = i18n.language as keyof typeof dayjsLocaleCodes
    const locale =
      code in dayjsLocaleCodes
        ? dayjsLocaleCodes[code]
        : dayjsLocaleCodes['en-GB']

    const onChange = (value: string | null) => {
      props.onChange(value ?? undefined)
    }

    return (
      <DatePickerInput
        ref={ref as Ref<HTMLButtonElement>}
        {...props}
        locale={locale}
        onChange={onChange}
        value={props.value ?? null}
        miw={150}
        clearable={!props.required}
      />
    )
  },
)

InputDatePure.displayName = 'InputDatePure'
